import React, { useState, useEffect } from 'react';
import { Clock, Shield } from 'lucide-react';
import { useAuth } from '../../../common/context/AuthContext';

const SessionTimerIndicator = ({ module }) => {
  const { userRole } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showWarning, setShowWarning] = useState(false);

  const SESSION_DURATION = {
    admin: 30 * 60 * 1000, // 30 minutes
    manager: 15 * 60 * 1000 // 15 minutes
  };

  const WARNING_THRESHOLD = 5 * 60 * 1000; // Show warning at 5 minutes remaining

  useEffect(() => {
    const updateTimer = () => {
      const sessionKey = `pin_session_${module || 'default'}`;
      const sessionData = sessionStorage.getItem(sessionKey);

      if (sessionData) {
        try {
          const { role, timestamp } = JSON.parse(sessionData);
          const now = Date.now();
          const elapsed = now - timestamp;
          const maxDuration = SESSION_DURATION[role] || SESSION_DURATION.manager;
          const remaining = maxDuration - elapsed;

          if (remaining > 0) {
            setTimeRemaining(remaining);
            setShowWarning(remaining <= WARNING_THRESHOLD);
          } else {
            setTimeRemaining(0);
          }
        } catch (error) {
          console.error('Error parsing session data:', error);
          setTimeRemaining(null);
        }
      } else {
        setTimeRemaining(null);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000); // Update every second

    return () => clearInterval(interval);
  }, [module]);

  const formatTime = (ms) => {
    if (ms <= 0) return '0:00';
    
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    if (!timeRemaining) return 0;
    
    const maxDuration = SESSION_DURATION[userRole] || SESSION_DURATION.manager;
    return (timeRemaining / maxDuration) * 100;
  };

  if (timeRemaining === null) return null;

  const progress = getProgressPercentage();
  const formattedTime = formatTime(timeRemaining);

  return (
    <div className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
      showWarning ? 'animate-pulse' : ''
    }`}>
      <div className={`bg-white rounded-2xl shadow-xl border-2 p-4 min-w-[200px] ${
        showWarning ? 'border-orange-400' : 'border-slate-200'
      }`}>
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            showWarning ? 'bg-orange-100' : 'bg-blue-50'
          }`}>
            {showWarning ? (
              <Clock className="text-orange-600" size={20} />
            ) : (
              <Shield className="text-blue-600" size={20} />
            )}
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Session Time
            </p>
            <p className={`text-lg font-bold ${
              showWarning ? 'text-orange-600' : 'text-slate-900'
            }`}>
              {formattedTime}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative w-full h-2 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className={`absolute top-0 left-0 h-full transition-all duration-1000 rounded-full ${
              showWarning ? 'bg-gradient-to-r from-orange-400 to-red-500' : 'bg-gradient-to-r from-blue-500 to-blue-600'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {showWarning && (
          <p className="text-[10px] text-orange-700 font-semibold mt-2 text-center">
            Session expiring soon!
          </p>
        )}

        <div className="mt-2 flex items-center justify-center gap-1 text-[9px] text-slate-400 font-medium">
          <Shield size={10} />
          <span>{userRole === 'admin' ? 'Admin' : 'Manager'} Access</span>
        </div>
      </div>
    </div>
  );
};

export default SessionTimerIndicator;